/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { moduleListService } from "../../services/module-list.service";
import LogicContext from "../../Context/LogicContext";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import { useNavigate } from "react-router-dom";
import { GetParentMenu, SetParentMenu } from "../../store/parentMenuAtom";
import { GetChildMenuEdit, SetChildMenuEdit } from "../../store/childMenuAtom";


const AddNewChildMenuPage = () => {
    const myPromiseModal = createModal(GeneralModal);
    const logicContext = useContext(LogicContext);
    const navigate = useNavigate();

    const getParentMenuData = GetParentMenu();
    const setParentMenuData = SetParentMenu();
    const editData = GetChildMenuEdit();
    const setChildMenuEdit = SetChildMenuEdit();
    const isEditMode = !!editData?.menu_id;

    const moduleList = getParentMenuData?.module_info || [];
    const allParentMenus = getParentMenuData?.menu_parent_info || [];

    const [selectedModuleId, setSelectedModuleId] = useState("");

    const filteredParentMenus = selectedModuleId
        ? allParentMenus.filter(m => String(m.module_id) === String(selectedModuleId))
        : [];

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        defaultValues: {
            menu_parent_id: "",
            menu_child_name: "",
            function_name: "",
            menu_child_print_sequence: "",
            menu_child_status: "A",
        },
    });

    useEffect(() => {
        if (allParentMenus.length === 0) {
            loadParentMenuList();
        }
    }, []);

    useEffect(() => {
        if (isEditMode && allParentMenus.length > 0) {
            setSelectedModuleId(String(editData.module_id));
            setValue("menu_child_name", editData.menu_name);
            setValue("function_name", editData.function_name);
            setValue("menu_child_print_sequence", editData.menu_parent_print_sequence);
            setValue("menu_child_status", editData.status_id);
        }
    }, [isEditMode, allParentMenus.length]);

    // Set parent menu only after selectedModuleId is committed so filteredParentMenus is populated
    useEffect(() => {
        if (isEditMode && selectedModuleId === String(editData?.module_id)) {
            setValue("menu_parent_id", String(editData.menu_parent_id));
        }
    }, [selectedModuleId]);

    const loadParentMenuList = useCallback(async () => {
        try {
            const response = await moduleListService.getParentMenuList();
            if (response?.data) {
                setParentMenuData(response.data);
            }
        } catch (err) {
            console.error(err);
            logicContext.showToast("Failed to load parent menu list", "error");
        }
    }, []);

    const handleModuleChange = (e) => {
        setSelectedModuleId(e.target.value);
        setValue("menu_parent_id", "");
    };

    const onSubmit = async (formData) => {
        const confirmed = await myPromiseModal({
            title: isEditMode ? "Update Child Menu" : "Save Child Menu",
            body: isEditMode ? "Would you like to update this child menu?" : "Would you like to save this child menu?",
            showInput: false,
        });

        if (!confirmed?.decision) return;

        const payload = {
            menu_parent_id: Number(formData.menu_parent_id),
            menu_child_name: formData.menu_child_name.trim(),
            function_name: formData.function_name.trim(),
            menu_child_print_sequence: Number(formData.menu_child_print_sequence),
            menu_child_status: formData.menu_child_status,
            option: isEditMode ? 2 : 1,
            ...(isEditMode && { menu_child_id: editData.menu_id }),
        };

        const response = await moduleListService.saveChildMenu(payload);
        const result = logicContext.httpResponseManager(
            response.data?.[0] || response.data?.result_status?.[0]
        );

        if (result.message_id === 1) {
            logicContext.showToast(
                result.message_text || (isEditMode ? "Child Menu updated successfully!" : "Child Menu created successfully!"),
                "success"
            );
            setChildMenuEdit({});
            sessionStorage.setItem("reloadPage", "true");
            navigate(-1);
        } else {
            logicContext.showToast(result.message_text || "Operation failed", "error");
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-2xl mx-auto">

                {/* Header */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-3"
                    >
                        ← Back
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800">
                        {isEditMode ? "Edit Child Menu" : "Add New Child Menu"}
                    </h2>
                    <p className="text-sm text-gray-400 mt-0.5">
                        {isEditMode ? "Update the details for this child menu entry" : "Fill in the details to create a new child menu entry"}
                    </p>
                </div>

                {/* Form Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <div className="space-y-5">

                            {/* Step 1 — Select Module */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Module <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedModuleId}
                                    onChange={handleModuleChange}
                                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                                >
                                    <option value="">— Select Module —</option>
                                    {moduleList.map((mod) => (
                                        <option key={mod.module_id} value={mod.module_id}>
                                            {mod.module_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Step 2 — Select Parent Menu (filtered by module) */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Parent Menu <span className="text-red-500">*</span>
                                </label>
                                <select
                                    {...register("menu_parent_id", { required: "Please select a parent menu" })}
                                    disabled={!selectedModuleId}
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                                        disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed
                                        ${errors.menu_parent_id ? "border-red-400 focus:ring-red-400" : "border-gray-200"}`}
                                >
                                    <option value="">
                                        {selectedModuleId ? "— Select Parent Menu —" : "— Select a module first —"}
                                    </option>
                                    {filteredParentMenus.map((item) => (
                                        <option key={item.menu_parent_id} value={item.menu_parent_id}>
                                            {item.menu_parent_name} (ID: {item.menu_parent_id})
                                        </option>
                                    ))}
                                </select>
                                {errors.menu_parent_id && (
                                    <p className="mt-1 text-xs text-red-500">{errors.menu_parent_id.message}</p>
                                )}
                            </div>

                            {/* Menu Child ID */}


                            {/* Menu Child Name */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Menu Child Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register("menu_child_name", {
                                        required: "Child menu name is required",
                                        minLength: { value: 2, message: "Name must be at least 2 characters" },
                                        maxLength: { value: 100, message: "Name must be 100 characters or fewer" },
                                    })}
                                    placeholder="e.g. Test"
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                                        ${errors.menu_child_name ? "border-red-400 focus:ring-red-400" : "border-gray-200"}`}
                                />
                                {errors.menu_child_name && (
                                    <p className="mt-1 text-xs text-red-500">{errors.menu_child_name.message}</p>
                                )}
                            </div>

                            {/* Function Name */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Function Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register("function_name", {
                                        required: "Function name is required",
                                        minLength: { value: 2, message: "Function name must be at least 2 characters" },
                                        maxLength: { value: 100, message: "Function name must be 100 characters or fewer" },
                                    })}
                                    placeholder="e.g. manual_journals"
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                                        ${errors.function_name ? "border-red-400 focus:ring-red-400" : "border-gray-200"}`}
                                />
                                {errors.function_name && (
                                    <p className="mt-1 text-xs text-red-500">{errors.function_name.message}</p>
                                )}
                            </div>

                            {/* Print Sequence */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Print Sequence <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    {...register("menu_child_print_sequence", {
                                        required: "Print sequence is required",
                                        min: { value: 1, message: "Sequence must be at least 1" },
                                    })}
                                    placeholder="e.g. 1"
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                                        ${errors.menu_child_print_sequence ? "border-red-400 focus:ring-red-400" : "border-gray-200"}`}
                                />
                                {errors.menu_child_print_sequence && (
                                    <p className="mt-1 text-xs text-red-500">{errors.menu_child_print_sequence.message}</p>
                                )}
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    Status <span className="text-red-500">*</span>
                                </label>
                                <select
                                    {...register("menu_child_status", { required: "Status is required" })}
                                    className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition
                                        ${errors.menu_child_status ? "border-red-400 focus:ring-red-400" : "border-gray-200"}`}
                                >
                                    <option value="A">Active</option>
                                    <option value="I">Inactive</option>
                                </select>
                                {errors.menu_child_status && (
                                    <p className="mt-1 text-xs text-red-500">{errors.menu_child_status.message}</p>
                                )}
                            </div>

                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 mt-8 pt-5 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => { setChildMenuEdit({}); navigate(-1); }}
                                className="px-5 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm"
                            >
                                {isSubmitting ? "Saving…" : isEditMode ? "Update Child Menu" : "Save Child Menu"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};


export default React.memo(AddNewChildMenuPage);
